/**
 * CLE-2842 — the Adjustment Rules list shows one row per rule *bucket* instead of one per copy.
 *
 * Ani copies one rule to many providers and payers, and every copy is its own row; the backend
 * tags rows that are the same rule — same code lists, same terms, same enabled state — with a
 * shared `groupKey`, whatever their provider, payer or name. Only the three types with a
 * group-edit endpoint are collapsed. Everything else — other types, and rows whose `groupKey` is
 * null (Typical / Fallback) — is passed through untouched.
 */
import { ALL_PAYERS_LABEL } from "../../PayerRulesPage/helpers/ruleApplicability";
import {
  bucketNames,
  byProviderThenPayer,
} from "../../PayerRulesPage/helpers/ruleBucket";

export const GROUPABLE_RULE_TYPES = ["MPPR", "Case Rate", "Package"];

export interface IGroupableRuleRow {
  id: string;
  ruleType: string;
  groupKey?: string | null;
  /** Null on a provider's "All payers" row. */
  payerId?: number | string | null;
  payerName?: string;
  providerId?: string | null;
  providerName?: string;
  ruleName?: string;
  /** What the Rule Name filter reads: the rule's name, or every member name of a bucket. */
  ruleNamesSearchText?: string;
  attachments?: { id: string }[];
  modifiedFields?: string[];
}

/** One provider of a bucket and the payers it covers there. */
export interface IRuleBucketProviderPairs {
  providerName: string;
  payerNames: string[];
}

export interface IRuleBucketFields<TRow> {
  /**
   * Every row in the bucket, in the server's order — provider name, then the All payers row
   * (payerId null), then payer name, then id. The display row is `members[0]`.
   */
  members: TRow[];
  memberIds: string[];
  /** Distinct payer names, All payers first. */
  payerNames: string[];
  /** Distinct provider names, alphabetical. */
  providerNames: string[];
  /** Distinct member names (the name is not part of the rule), longest first. */
  ruleNames: string[];
  nameCount: number;
  /** The provider → payers pairs, grouped by provider. */
  pairs: IRuleBucketProviderPairs[];
  /** The distinct provider / payer names joined with ", " — what the text filters read. */
  providerName: string;
  payerName: string;
  /** Every distinct member name joined with ", " — what the Rule Name filter reads. */
  ruleNamesSearchText: string;
}

export type TRuleBucketRow<TRow> = TRow & IRuleBucketFields<TRow>;

/** Every item of every list, once, in first-seen order. */
const unionBy = <TItem>(lists: TItem[][], keyOf: (item: TItem) => string) => {
  const seen = new Set<string>();
  return lists.flat().filter((item) => {
    const key = keyOf(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const distinct = (values: string[]) => Array.from(new Set(values));

const bucketKeyOf = (row: IGroupableRuleRow): string | null =>
  GROUPABLE_RULE_TYPES.includes(row.ruleType) && row.groupKey
    ? row.groupKey
    : null;

export function isRuleBucketRow<TRow>(row: TRow): row is TRuleBucketRow<TRow> {
  return Array.isArray((row as Partial<IRuleBucketFields<TRow>>)?.members);
}

const payerLabelOf = (row: IGroupableRuleRow) =>
  row.payerId === null
    ? row.payerName || ALL_PAYERS_LABEL
    : row.payerName ?? "—";

/**
 * Collapses rows sharing a `groupKey` into one display row: the first member (server order)
 * spread, plus the members and their distinct names, providers and payers. The text the
 * existing filters and sort read is rebuilt so a bucket matches when any member does:
 * `providerName` / `payerName` are the distinct names joined with ", ", and `ruleName` is the
 * LONGEST distinct member name ("Hospital Outpatient: T codes" over "T"), ties alphabetically.
 * A bucket keeps the position of its first row in the input.
 */
export function groupRuleRows<TRow extends IGroupableRuleRow>(
  rows: TRow[]
): Array<TRow | TRuleBucketRow<TRow>> {
  const membersByKey = new Map<string, TRow[]>();
  rows.forEach((row) => {
    const key = bucketKeyOf(row);
    if (!key) return;
    const members = membersByKey.get(key);
    if (members) members.push(row);
    else membersByKey.set(key, [row]);
  });

  const emitted = new Set<string>();
  const result: Array<TRow | TRuleBucketRow<TRow>> = [];
  rows.forEach((row) => {
    const key = bucketKeyOf(row);
    if (!key) {
      result.push(row);
      return;
    }
    if (emitted.has(key)) return;
    emitted.add(key);
    // The server's member order (shared with the edit page): the first one opens on Edit.
    const members = [...(membersByKey.get(key) ?? [row])].sort(
      byProviderThenPayer
    );

    const payerNames = distinct(members.map(payerLabelOf)).sort(
      (a, b) =>
        Number(a !== ALL_PAYERS_LABEL) - Number(b !== ALL_PAYERS_LABEL) ||
        a.localeCompare(b)
    );
    const providerNames = distinct(
      members.map((member) => member.providerName ?? "—")
    ).sort((a, b) => a.localeCompare(b));
    // Longest first, ties alphabetically — the same rule the edit page opens its name on.
    const ruleNames = bucketNames(members.map((member) => member.ruleName));
    const pairs = providerNames.map((providerName) => ({
      providerName,
      payerNames: members
        .filter((member) => (member.providerName ?? "—") === providerName)
        .map(payerLabelOf),
    }));

    // Contract-language attachments and modified fields are per row, so the bucket row shows
    // every member's — not just the first one's. A field no member carries stays absent.
    const perRow: Partial<IGroupableRuleRow> = {};
    if (members.some((member) => Array.isArray(member.attachments))) {
      perRow.attachments = unionBy(
        members.map((member) => member.attachments ?? []),
        (attachment) => attachment.id
      );
    }
    if (members.some((member) => Array.isArray(member.modifiedFields))) {
      perRow.modifiedFields = unionBy(
        members.map((member) => member.modifiedFields ?? []),
        (field) => field
      );
    }
    result.push({
      ...members[0],
      ...perRow,
      ...(ruleNames.length ? { ruleName: ruleNames[0] } : {}),
      members,
      memberIds: members.map((member) => member.id),
      payerNames,
      providerNames,
      ruleNames,
      nameCount: Math.max(ruleNames.length, 1),
      pairs,
      payerName: payerNames.join(", "),
      providerName: providerNames.join(", "),
      ruleNamesSearchText: ruleNames.length
        ? ruleNames.join(", ")
        : members[0].ruleName ?? "",
    });
  });
  return result;
}
